/**
 * Layout presets (grid city, village along a path, radial town, fields, castle walls) and templates.
 * Pure functions: area/path points + parameters → plan objects to create (block coordinates).
 */
import { bounds, gridBlocks, pathGrid, polygonGrid, type Pt } from "./geometry.ts";
import { schematicFootprint, schematicDims, turnSchematic, type SchematicRef } from "./schematic.ts";

/** An object a layout wants to create (the store fills in id, dimension, timestamps, …). */
export interface LayoutObject {
  kind: "path" | "area" | "building" | "marker";
  name: string;
  points: Pt[];
  width?: number;
  height?: number;
  material?: string;
  color?: string;
  y?: number;
}

export type PresetId = "grid" | "village" | "radial" | "fields" | "walls";

export interface PresetParam {
  key: string; label: string; min: number; max: number; step: number; def: number; unit?: string; scales?: boolean;
  /** a choice instead of a slider (value = index) */
  options?: string[];
  /** only relevant when another parameter has one of these values */
  show?: { key: string; values: number[] };
  /** not relevant when another parameter has one of these values */
  hide?: { key: string; values: number[] };
}

/** Crops for the "Farm fields" preset (material = the crop block, drawn on farmland). */
export const CROPS: { name: string; material: string }[] = [
  { name: "Wheat", material: "minecraft:wheat" },
  { name: "Carrots", material: "minecraft:carrots" },
  { name: "Potatoes", material: "minecraft:potatoes" },
  { name: "Beetroot", material: "minecraft:beetroots" },
  { name: "Pumpkins", material: "minecraft:pumpkin" },
  { name: "Melons", material: "minecraft:melon" },
  { name: "Sugar cane", material: "minecraft:sugar_cane" },
  { name: "Sweet berries", material: "minecraft:sweet_berry_bush" },
  { name: "Torchflowers", material: "minecraft:torchflower_crop" },
  { name: "Pitcher plants", material: "minecraft:pitcher_crop" },
  { name: "Nether wart", material: "minecraft:nether_wart" },
  { name: "Cactus", material: "minecraft:cactus" },
  { name: "Just farmland", material: "minecraft:farmland" },
];
/** "Mixed" = each field gets the next of these */
const MIXED = ["minecraft:wheat", "minecraft:carrots", "minecraft:potatoes", "minecraft:beetroots"];

export interface Preset {
  id: PresetId;
  name: string;
  /** what it fills: an area or a path */
  on: "area" | "path";
  description: string;
  params: PresetParam[];
}

export const PRESETS: Preset[] = [
  {
    id: "grid", name: "Grid city", on: "area", description: "Straight streets, blocks of plots facing the streets.",
    params: [
      { key: "street", label: "Street width", min: 1, max: 9, step: 1, def: 3, unit: "blocks", scales: true },
      { key: "block", label: "Block size", min: 8, max: 80, step: 1, def: 24, unit: "blocks", scales: true },
      { key: "plot", label: "Plot size", min: 4, max: 32, step: 1, def: 9, unit: "blocks", scales: true },
      { key: "angle", label: "Rotation", min: 0, max: 85, step: 5, def: 0, unit: "°" },
      { key: "avenue", label: "Wide main avenue", min: 0, max: 1, step: 1, def: 1 },
      { key: "height", label: "House height", min: 3, max: 40, step: 1, def: 7, unit: "blocks" },
    ],
  },
  {
    id: "village", name: "Village along a path", on: "path", description: "Houses on both sides of a road.",
    params: [
      { key: "plotW", label: "Plot width (along the road)", min: 4, max: 30, step: 1, def: 9, unit: "blocks", scales: true },
      { key: "plotD", label: "Plot depth", min: 4, max: 30, step: 1, def: 8, unit: "blocks", scales: true },
      { key: "gap", label: "Gap between houses", min: 1, max: 20, step: 1, def: 4, unit: "blocks", scales: true },
      { key: "setback", label: "Distance from the road", min: 0, max: 10, step: 1, def: 2, unit: "blocks", scales: true },
      { key: "sides", label: "Both sides", min: 0, max: 1, step: 1, def: 1 },
      { key: "variation", label: "Variation", min: 0, max: 1, step: 0.1, def: 0.3 },
      { key: "height", label: "House height", min: 3, max: 30, step: 1, def: 6, unit: "blocks" },
    ],
  },
  {
    id: "radial", name: "Round town with plaza", on: "area", description: "Central square, ring roads and radial streets.",
    params: [
      { key: "plaza", label: "Plaza radius", min: 4, max: 40, step: 1, def: 10, unit: "blocks", scales: true },
      { key: "ring", label: "Distance between rings", min: 12, max: 60, step: 1, def: 22, unit: "blocks", scales: true },
      { key: "spokes", label: "Radial streets", min: 3, max: 12, step: 1, def: 6 },
      { key: "street", label: "Street width", min: 1, max: 7, step: 1, def: 3, unit: "blocks", scales: true },
      { key: "plot", label: "Plot size", min: 4, max: 20, step: 1, def: 7, unit: "blocks", scales: true },
      { key: "height", label: "House height", min: 3, max: 30, step: 1, def: 6, unit: "blocks" },
    ],
  },
  {
    id: "fields", name: "Farm fields", on: "area", description: "Crop fields — the whole area or in rows — with water channels or a river.",
    params: [
      { key: "mode", label: "Layout", min: 0, max: 1, step: 1, def: 0, options: ["Rows of fields with paths", "Fill the whole area"] },
      { key: "crop", label: "Crop", min: 0, max: CROPS.length, step: 1, def: 0, options: [...CROPS.map((c) => c.name), "Mixed (changes per field)"] },
      { key: "water", label: "Water", min: 0, max: 2, step: 1, def: 0, options: ["None", "Straight channels (every field hydrated)", "Winding river"] },
      { key: "spacing", label: "Channel every", min: 3, max: 30, step: 1, def: 9, unit: "blocks", show: { key: "water", values: [1] } },
      { key: "riverW", label: "River width", min: 1, max: 12, step: 1, def: 3, unit: "blocks", scales: true, show: { key: "water", values: [2] } },
      { key: "wiggle", label: "River curves", min: 0, max: 1, step: 0.1, def: 0.5, show: { key: "water", values: [2] } },
      { key: "fieldW", label: "Field width", min: 3, max: 40, step: 1, def: 9, unit: "blocks", scales: true, show: { key: "mode", values: [0] } },
      { key: "fieldD", label: "Field length", min: 3, max: 60, step: 1, def: 16, unit: "blocks", scales: true, show: { key: "mode", values: [0] }, hide: { key: "water", values: [1] } },
      { key: "path", label: "Path width", min: 1, max: 5, step: 1, def: 1, unit: "blocks", show: { key: "mode", values: [0] }, hide: { key: "water", values: [1] } },
      { key: "angle", label: "Rotation", min: 0, max: 85, step: 5, def: 0, unit: "°" },
    ],
  },
  {
    id: "walls", name: "Castle wall ring", on: "area", description: "Walls along the outline with towers.",
    params: [
      { key: "thick", label: "Wall thickness", min: 1, max: 5, step: 1, def: 2, unit: "blocks" },
      { key: "wallH", label: "Wall height", min: 4, max: 30, step: 1, def: 10, unit: "blocks" },
      { key: "tower", label: "Tower size", min: 3, max: 15, step: 1, def: 7, unit: "blocks" },
      { key: "every", label: "Tower every", min: 10, max: 120, step: 5, def: 40, unit: "blocks", scales: true },
    ],
  },
];

export type Params = Record<string, number>;

export function defaultParams(id: PresetId): Params {
  return Object.fromEntries(PRESETS.find((p) => p.id === id)!.params.map((p) => [p.key, p.def]));
}

/** Parameters with the "scales" ones multiplied (hamlet → city), clamped to whole blocks ≥ 1. */
export function scaledParams(id: PresetId, params: Params, scale: number): Params {
  const out: Params = { ...params };
  for (const p of PRESETS.find((x) => x.id === id)!.params) if (p.scales) out[p.key] = Math.max(1, Math.round((params[p.key] ?? p.def) * scale));
  return out;
}

// ---------- geometry helpers ----------

type V = [number, number];

function inside(x: number, z: number, poly: V[]) {
  let r = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, zi] = poly[i], [xj, zj] = poly[j];
    if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) r = !r;
  }
  return r;
}

/** block rectangle/polygon fully inside the area (all block centres of its corners inside) */
function polyInside(pts: V[], area: V[]) {
  return pts.every(([x, z]) => inside(x + 0.5, z + 0.5, area));
}

const rot = ([x, z]: V, c: V, a: number): V => {
  const s = Math.sin(a), k = Math.cos(a);
  return [c[0] + (x - c[0]) * k - (z - c[1]) * s, c[1] + (x - c[0]) * s + (z - c[1]) * k];
};
const round = (p: V): V => [Math.round(p[0]), Math.round(p[1])];

function centroid(pts: V[]): V {
  let x = 0, z = 0;
  for (const p of pts) { x += p[0]; z += p[1]; }
  return [x / pts.length, z / pts.length];
}

/** Parts of the horizontal line z = zc that lie inside the polygon, as [x0, x1] intervals. */
function scanX(poly: V[], zc: number): [number, number][] {
  const xs: number[] = [];
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, zi] = poly[i], [xj, zj] = poly[j];
    if ((zi > zc) !== (zj > zc)) xs.push(xi + ((zc - zi) * (xj - xi)) / (zj - zi));
  }
  xs.sort((a, b) => a - b);
  const out: [number, number][] = [];
  for (let k = 0; k + 1 < xs.length; k += 2) out.push([xs[k], xs[k + 1]]);
  return out;
}

/** Split a sampled polyline into the runs that are inside the area. */
function clipPolyline(pts: V[], area: V[], minLen = 3): V[][] {
  const runs: V[][] = [];
  let cur: V[] = [];
  for (const p of pts) {
    if (inside(p[0] + 0.5, p[1] + 0.5, area)) cur.push(p);
    else { if (cur.length > 1) runs.push(cur); cur = []; }
  }
  if (cur.length > 1) runs.push(cur);
  return runs.filter((r) => {
    let l = 0;
    for (let i = 1; i < r.length; i++) l += Math.hypot(r[i][0] - r[i - 1][0], r[i][1] - r[i - 1][1]);
    return l >= minLen;
  });
}

/** Drop repeated points after rounding. */
function dedupe(pts: V[]): V[] {
  const out: V[] = [];
  for (const p of pts) { const q = out[out.length - 1]; if (!q || q[0] !== p[0] || q[1] !== p[1]) out.push(p); }
  return out;
}

/** Rectangle of w × d blocks starting at p, running along unit vector u, depth along v. */
function orientedRect(p: V, u: V, v: V, w: number, d: number): V[] {
  const a = p, b: V = [p[0] + u[0] * (w - 1), p[1] + u[1] * (w - 1)];
  const c: V = [b[0] + v[0] * (d - 1), b[1] + v[1] * (d - 1)], e: V = [p[0] + v[0] * (d - 1), p[1] + v[1] * (d - 1)];
  return [a, b, c, e].map(round);
}

function overlaps(a: V[], b: V[]) {
  const ca = centroid(a), cb = centroid(b);
  return inside(ca[0], ca[1], b) || inside(cb[0], cb[1], a) || a.some((p) => inside(p[0], p[1], b)) || b.some((p) => inside(p[0], p[1], a));
}

// ---------- presets ----------

function gridCity(area: V[], p: Params): LayoutObject[] {
  const angle = ((p.angle ?? 0) * Math.PI) / 180;
  const c = centroid(area);
  // work in "grid space": rotate the area so the streets are axis-aligned there
  const A = area.map((q) => rot(q, c, -angle));
  const back = (q: V) => round(rot(q, c, angle));
  const b = bounds(A);
  const street = Math.max(1, p.street), block = Math.max(4, p.block), plot = Math.max(3, p.plot);
  const pitch = block + street;
  const half = Math.floor(street / 2);
  const out: LayoutObject[] = [];
  const xs: number[] = [], zs: number[] = [];
  for (let x = Math.round(b.x0) + half; x <= b.x1; x += pitch) xs.push(x);
  for (let z = Math.round(b.z0) + half; z <= b.z1; z += pitch) zs.push(z);
  const avenueZ = p.avenue ? zs[Math.floor(zs.length / 2)] : null;
  const avenueX = p.avenue ? xs[Math.floor(xs.length / 2)] : null;
  let n = 1;
  const streetAt = (a: V, bb: V, main: boolean) => {
    const pts = dedupe([back(a), back(bb)]);
    if (pts.length > 1) out.push({ kind: "path", name: main ? "Main avenue" : `Street ${n++}`, points: pts, width: main ? street * 2 + 1 : street, material: "minecraft:stone_bricks" });
  };
  for (const z of zs) for (const [x0, x1] of scanX(A, z + 0.5)) if (x1 - x0 >= 4) streetAt([Math.ceil(x0), z], [Math.floor(x1) - 1, z], z === avenueZ);
  for (const x of xs) for (const [z0, z1] of scanX(A.map(([u, v]) => [v, u] as V), x + 0.5)) if (z1 - z0 >= 4) streetAt([x, Math.ceil(z0)], [x, Math.floor(z1) - 1], x === avenueX);
  // plots: in each block, a ring of plots facing the streets (courtyard in the middle)
  const occupied = (s: number, wide: boolean): [number, number] => wide ? [s - half - half - 1, s - half + street + half] : [s - half, s - half + street - 1];
  /** free intervals between the streets along one axis */
  const free = (lines: number[], wideAt: number | null, lo: number, hi: number): [number, number][] => {
    const out2: [number, number][] = [];
    let cur = lo;
    for (const l of lines) {
      const [a, b2] = occupied(l, l === wideAt);
      if (a - 1 >= cur) out2.push([cur, a - 1]);
      cur = Math.max(cur, b2 + 1);
    }
    if (cur <= hi) out2.push([cur, hi]);
    return out2;
  };
  let h = 1;
  for (const [xa, xb] of free(xs, avenueX, Math.floor(b.x0), Math.ceil(b.x1)))
    for (const [za, zb] of free(zs, avenueZ, Math.floor(b.z0), Math.ceil(b.z1))) {
      const cw = xb - xa + 1, cd = zb - za + 1;
      if (cw < plot || cd < plot) continue;
      const nx = Math.max(1, Math.floor((cw + 1) / (plot + 1))), nz = Math.max(1, Math.floor((cd + 1) / (plot + 1)));
      const ox = xa + Math.floor((cw - (nx * (plot + 1) - 1)) / 2), oz = za + Math.floor((cd - (nz * (plot + 1) - 1)) / 2);
      for (let a = 0; a < nx; a++)
        for (let c2 = 0; c2 < nz; c2++) {
          if (nx > 2 && nz > 2 && a > 0 && a < nx - 1 && c2 > 0 && c2 < nz - 1) continue; // courtyard
          const x0 = ox + a * (plot + 1), z0 = oz + c2 * (plot + 1);
          const r: V[] = [[x0, z0], [x0 + plot - 1, z0], [x0 + plot - 1, z0 + plot - 1], [x0, z0 + plot - 1]];
          if (!polyInside(r, A)) continue;
          out.push({ kind: "building", name: `House ${h++}`, points: dedupe(r.map(back)), height: p.height ?? 7 });
        }
    }
  return out.filter((o) => o.kind !== "building" || o.points.length >= 3);
}

function village(path: V[], p: Params): LayoutObject[] {
  const out: LayoutObject[] = [];
  const W = Math.max(3, p.plotW), D = Math.max(3, p.plotD), gap = Math.max(0, p.gap), setback = Math.max(0, p.setback);
  const roadHalf = (p.roadWidth ?? 3) / 2;
  const houses: V[][] = [];
  let seed = 12345;
  const rnd = () => ((seed = (seed * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff);
  let n = 1;
  for (let i = 0; i + 1 < path.length; i++) {
    const a: V = [path[i][0] + 0.5, path[i][1] + 0.5], b: V = [path[i + 1][0] + 0.5, path[i + 1][1] + 0.5];
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (len < W) continue;
    const u: V = [(b[0] - a[0]) / len, (b[1] - a[1]) / len];
    for (const side of p.sides ? [1, -1] : [1]) {
      const v: V = [-u[1] * side, u[0] * side];
      for (let t = gap / 2; t + W <= len; t += W + gap) {
        const vary = 1 + (rnd() - 0.5) * 2 * (p.variation ?? 0) * 0.4;
        const d = Math.max(3, Math.round(D * vary)), w = Math.max(3, Math.round(W * (2 - vary)));
        const off = roadHalf + setback + 0.5;
        const start: V = [a[0] + u[0] * t + v[0] * off - 0.5, a[1] + u[1] * t + v[1] * off - 0.5];
        const rect = orientedRect(start, u, v, Math.min(w, W), d);
        if (houses.some((h) => overlaps(h, rect))) continue;
        houses.push(rect);
        out.push({ kind: "building", name: `House ${n++}`, points: rect, height: p.height ?? 6 });
      }
    }
  }
  return out;
}

function circle(c: V, r: number, n = Math.max(16, Math.round(r * 1.2))): V[] {
  const out: V[] = [];
  for (let i = 0; i < n; i++) out.push([c[0] + Math.cos((i / n) * Math.PI * 2) * r, c[1] + Math.sin((i / n) * Math.PI * 2) * r]);
  return out;
}

function radial(area: V[], p: Params): LayoutObject[] {
  const c = centroid(area);
  const b = bounds(area);
  const R = Math.hypot(b.x1 - b.x0, b.z1 - b.z0) / 2;
  const street = Math.max(1, p.street), plaza = Math.max(3, p.plaza), ring = Math.max(8, p.ring), plot = Math.max(3, p.plot);
  const out: LayoutObject[] = [];
  const plazaPoly = dedupe(circle(c, plaza, 20).map(round));
  if (polyInside(plazaPoly, area)) out.push({ kind: "area", name: "Plaza", points: plazaPoly, material: "minecraft:polished_andesite" });
  // ring roads
  const radii: number[] = [];
  for (let r = plaza + street / 2 + 1; r < R; r += ring) radii.push(r);
  let n = 1;
  for (const r of radii) {
    const ptsRing = circle(c, r).map(round);
    for (const run of clipPolyline([...ptsRing, ptsRing[0]], area)) out.push({ kind: "path", name: `Ring road ${n++}`, points: dedupe(run), width: street, material: "minecraft:stone_bricks" });
  }
  // spokes
  const S = Math.max(3, Math.round(p.spokes));
  for (let k = 0; k < S; k++) {
    const a = (k / S) * Math.PI * 2;
    const ray: V[] = [];
    for (let r = plaza; r <= R; r += 2) ray.push(round([c[0] + Math.cos(a) * r, c[1] + Math.sin(a) * r]));
    for (const run of clipPolyline(ray, area)) out.push({ kind: "path", name: `Street ${k + 1}`, points: [run[0], run[run.length - 1]], width: street, material: "minecraft:stone_bricks" });
  }
  // plots between the rings, facing the ring road, away from the spokes
  const houses: V[][] = [];
  let h = 1;
  for (const r of radii) {
    for (const outward of [1, -1]) {
      const rr = r + outward * (street / 2 + 1 + plot / 2);
      if (rr < plaza + plot) continue;
      const steps = Math.floor((2 * Math.PI * rr) / (plot + 2));
      for (let i = 0; i < steps; i++) {
        const a = ((i + 0.5) / steps) * Math.PI * 2;
        // keep clear of the radial streets
        const nearest = Math.min(...Array.from({ length: S }, (_, k) => Math.abs(((a - (k / S) * Math.PI * 2 + Math.PI * 3) % (Math.PI * 2)) - Math.PI)));
        if (nearest * rr < plot / 2 + street) continue;
        const u: V = [-Math.sin(a), Math.cos(a)], v: V = [Math.cos(a) * outward, Math.sin(a) * outward];
        const centre: V = [c[0] + Math.cos(a) * rr, c[1] + Math.sin(a) * rr];
        const start: V = [centre[0] - u[0] * (plot / 2) - v[0] * (plot / 2), centre[1] - u[1] * (plot / 2) - v[1] * (plot / 2)];
        const rect = orientedRect(start, u, v, plot, plot);
        if (!polyInside(rect, area) || houses.some((x) => overlaps(x, rect))) continue;
        houses.push(rect);
        out.push({ kind: "building", name: `House ${h++}`, points: rect, height: p.height ?? 6 });
      }
    }
  }
  return out;
}

/** A water line crossing the fields in grid space: centre z at x, half width. */
interface Cut { z: (x: number) => number; hw: number }

/** Parts of one column (x) of the area between two cuts → polygons (runs of consecutive columns). */
function regionsBetween(A: V[], cuts: Cut[], b: ReturnType<typeof bounds>): V[][] {
  const colIv = (x: number): [number, number] | null => {
    const iv = scanX(A.map(([u, v]) => [v, u] as V), x + 0.5);
    if (!iv.length) return null;
    const best = iv.sort((a, c) => (c[1] - c[0]) - (a[1] - a[0]))[0];
    return [Math.ceil(best[0] - 0.5), Math.floor(best[1] - 0.5)];
  };
  const out: V[][] = [];
  for (let i = 0; i <= cuts.length; i++) {
    let run: { x: number; lo: number; hi: number }[] = [];
    const flush = () => {
      if (run.length >= 2) {
        const top = run.map((r) => [r.x, r.lo] as V), bottom = run.slice().reverse().map((r) => [r.x, r.hi] as V);
        out.push(simplify([...top, ...bottom]));
      }
      run = [];
    };
    // the limit over x-1..x+1: an outline edge between two columns then never dips into the water
    const loAt = (x: number) => (i > 0 ? Math.max(...[x - 1, x, x + 1].map((u) => Math.ceil(cuts[i - 1].z(u) + cuts[i - 1].hw + 0.5))) : -Infinity);
    const hiAt = (x: number) => (i < cuts.length ? Math.min(...[x - 1, x, x + 1].map((u) => Math.floor(cuts[i].z(u) - cuts[i].hw - 0.5))) : Infinity);
    for (let x = Math.ceil(b.x0); x <= Math.floor(b.x1); x++) {
      const iv = colIv(x);
      if (!iv) { flush(); continue; }
      const lo = Math.max(iv[0], loAt(x)), hi = Math.min(iv[1], hiAt(x));
      if (hi - lo < 1) { flush(); continue; }
      run.push({ x, lo, hi });
    }
    flush();
  }
  return out.filter((r) => r.length >= 3);
}

/** Drop points that lie on a straight line between their neighbours. */
function simplify(pts: V[]): V[] {
  const out: V[] = [];
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const a = pts[(i - 1 + n) % n], p = pts[i], c = pts[(i + 1) % n];
    const cross = (p[0] - a[0]) * (c[1] - p[1]) - (p[1] - a[1]) * (c[0] - p[0]);
    if (Math.abs(cross) > 1e-9) out.push(p);
  }
  return out;
}

function fields(area: V[], p: Params): LayoutObject[] {
  const angle = ((p.angle ?? 0) * Math.PI) / 180;
  const c = centroid(area);
  const A = area.map((q) => rot(q, c, -angle));
  const back = (q: V) => round(rot(q, c, angle));
  const b = bounds(A);
  const out: LayoutObject[] = [];
  const crop = Math.round(p.crop ?? 0);
  let fieldNo = 0;
  const cropOf = () => {
    fieldNo++;
    if (crop >= CROPS.length) return { name: MIXED.map((m) => CROPS.find((x) => x.material === m)!.name)[(fieldNo - 1) % MIXED.length], material: MIXED[(fieldNo - 1) % MIXED.length] };
    return CROPS[crop];
  };
  const fieldName = (name: string) => `${name === "Just farmland" ? "Field" : `${name} field`} ${fieldNo}`;

  // ---- water ----
  const cuts: Cut[] = [];
  const water = Math.round(p.water ?? 0);
  if (water === 1) {
    const every = Math.max(3, Math.round(p.spacing ?? 9));
    let k = 1;
    for (let z = Math.floor(b.z0) + Math.floor(every / 2); z <= b.z1 - 1; z += every) {
      cuts.push({ z: () => z, hw: 0 });
      for (const [x0, x1] of scanX(A, z + 0.5)) if (x1 - x0 > 3) out.push({ kind: "path", name: `Water channel ${k++}`, points: dedupe([back([Math.ceil(x0), z]), back([Math.floor(x1) - 1, z])]), width: 1, material: "minecraft:water" });
    }
  } else if (water === 2) {
    const rw = Math.max(1, Math.round(p.riverW ?? 3));
    const amp = (p.wiggle ?? 0.5) * (b.z1 - b.z0) * 0.22;
    const lambda = Math.max(40, (b.x1 - b.x0) / 1.3);
    const cz = (b.z0 + b.z1) / 2;
    const f = (x: number) => cz + amp * Math.sin((2 * Math.PI * (x - b.x0)) / lambda + 0.6) + amp * 0.35 * Math.sin((2 * Math.PI * (x - b.x0)) / (lambda * 0.37) + 1.7);
    const line: V[] = [];
    for (let x = Math.floor(b.x0) - 3; x <= Math.ceil(b.x1) + 3; x += 3) line.push([x, Math.round(f(x))]);
    // the fields follow exactly the drawn (rounded) river line, not the smooth curve
    const zAt = (x: number) => {
      const i = Math.max(0, Math.min(line.length - 2, Math.floor((x - line[0][0]) / 3)));
      const [ax, az] = line[i], [bx, bz] = line[i + 1];
      return az + ((x - ax) / (bx - ax)) * (bz - az);
    };
    cuts.push({ z: zAt, hw: rw / 2 + 0.5 });
    for (const run of clipPolyline(line, A)) out.push({ kind: "path", name: "River", points: dedupe(run.map(back)), width: rw, material: "minecraft:water" });
  }
  const inBand = (x0: number, x1: number, z0: number, z1: number) =>
    cuts.some((cut) => { for (let x = x0; x <= x1; x += 1) { const zc = cut.z(x); if (z1 >= zc - cut.hw - 0.5 && z0 <= zc + cut.hw + 0.5) return true; } return false; });

  if (Math.round(p.mode ?? 0) === 1) {
    // ---- the whole area is one field (split by the water) ----
    const regions = cuts.length ? regionsBetween(A, cuts, b) : [A.map(round)];
    for (const r of regions) {
      const cp = cropOf();
      const pts = dedupe(r.map(back));
      if (pts.length >= 3) out.push({ kind: "area", name: fieldName(cp.name), points: pts, material: cp.material });
    }
    return out;
  }

  // ---- rows of fields with paths between them ----
  const fw = Math.max(2, p.fieldW), pw = Math.max(1, p.path);
  let fd = Math.max(2, p.fieldD);
  let z0 = Math.floor(b.z0) + 1, step = fd + pw;
  if (water === 1) {
    // rows sit between the channels: the channel is the gap, every field is hydrated
    const every = Math.max(3, Math.round(p.spacing ?? 9));
    fd = every - 1;
    z0 = Math.floor(b.z0) + Math.floor(every / 2) - every + 1;
    step = every;
  }
  let m = 1;
  for (let z = z0; z + fd <= b.z1; z += step) {
    if (z < b.z0) continue;
    for (let x = Math.floor(b.x0) + 1; x + fw <= b.x1; x += fw + pw) {
      const rect: V[] = [[x, z], [x + fw - 1, z], [x + fw - 1, z + fd - 1], [x, z + fd - 1]];
      if (!polyInside(rect, A) || inBand(x, x + fw - 1, z, z + fd - 1)) continue;
      const cp = cropOf();
      out.push({ kind: "area", name: fieldName(cp.name), points: dedupe(rect.map(back)), material: cp.material });
    }
    if (water === 1) continue; // the channels are the paths
    const pz = z + fd + Math.floor(pw / 2);
    for (const [x0, x1] of scanX(A, pz + 0.5)) if (x1 - x0 > 4) out.push({ kind: "path", name: `Field path ${m++}`, points: dedupe([back([Math.ceil(x0), pz]), back([Math.floor(x1) - 1, pz])]), width: pw, material: "minecraft:dirt_path" });
  }
  return out.filter((o) => o.points.length >= (o.kind === "area" ? 3 : 2));
}

function walls(area: V[], p: Params): LayoutObject[] {
  const t = Math.max(1, p.thick), H = Math.max(2, p.wallH), T = Math.max(3, p.tower), every = Math.max(8, p.every);
  const out: LayoutObject[] = [];
  const n = area.length;
  // make the outline run clockwise so "inward" is consistent
  let s = 0;
  for (let i = 0, j = n - 1; i < n; j = i++) s += (area[j][0] - area[i][0]) * (area[j][1] + area[i][1]);
  const pts = s > 0 ? area.slice() : area.slice().reverse();
  const towers: V[] = [];
  let w = 1;
  for (let i = 0; i < n; i++) {
    const a = pts[i], b = pts[(i + 1) % n];
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (len < 1) continue;
    const u: V = [(b[0] - a[0]) / len, (b[1] - a[1]) / len];
    const inward: V = [u[1], -u[0]];
    const rect = orientedRect(a, u, inward, Math.round(len) + 1, t);
    out.push({ kind: "building", name: `Wall ${w++}`, points: rect, height: H, material: "minecraft:stone_bricks" });
    towers.push(a);
    for (let d = every; d < len - T; d += every) towers.push([a[0] + u[0] * d, a[1] + u[1] * d]);
  }
  towers.forEach((c, i) => {
    const h = Math.floor(T / 2);
    out.push({ kind: "building", name: `Tower ${i + 1}`, points: [[c[0] - h, c[1] - h], [c[0] - h + T - 1, c[1] - h], [c[0] - h + T - 1, c[1] - h + T - 1], [c[0] - h, c[1] - h + T - 1]].map((q) => round(q as V)), height: H + 5, material: "minecraft:cobblestone" });
  });
  return out;
}

/**
 * Final safety net against rounding: drop buildings that touch a road, a paved area or another building
 * (block by block). `blockers` = extra roads (e.g. the path a village is built along).
 */
export function removeOverlaps(objs: LayoutObject[], blockers: LayoutObject[] = []): LayoutObject[] {
  const taken = new Set<string>();
  const cellsOf = (o: LayoutObject) => gridBlocks(o.kind === "path" ? pathGrid(o.points, o.width ?? 1) : polygonGrid(o.points)).map(([x, z]) => `${x},${z}`);
  for (const o of [...blockers, ...objs]) if (o.kind !== "building") for (const c of cellsOf(o)) taken.add(c);
  return objs.filter((o) => {
    if (o.kind !== "building") return true;
    const cs = cellsOf(o);
    if (cs.some((c) => taken.has(c))) return false;
    for (const c of cs) taken.add(c);
    return true;
  });
}

/** Run a preset. `shape` = the area polygon or path polyline it fills. */
export function runPreset(id: PresetId, shape: Pt[], params: Params, extra: { roadWidth?: number } = {}): LayoutObject[] {
  const s = shape as V[];
  switch (id) {
    case "grid": return removeOverlaps(gridCity(s, params));
    case "village": return removeOverlaps(village(s, { ...params, roadWidth: extra.roadWidth ?? 3 }), [{ kind: "path", name: "road", points: shape, width: extra.roadWidth ?? 3 }]);
    case "radial": return removeOverlaps(radial(s, params));
    case "fields": return fields(s, params);
    case "walls": return walls(s, params); // towers overlap the walls on purpose
  }
}

// ---------- templates ----------

export interface TemplateObject extends LayoutObject {
  status?: string;
  notes?: string;
  storey?: number;
  sourceUrl?: string;
  /** a building from a schematic keeps its size when the template is scaled, and turns with it */
  schematic?: SchematicRef;
}

export interface Template {
  id: string;
  name: string;
  objects: TemplateObject[];
  builtin?: boolean;
  author?: string;
  createdAt?: number;
}

/** Objects → template with points relative to the top-left of their bounding box. */
export function makeTemplate(id: string, name: string, objs: TemplateObject[], author = ""): Template {
  const all = objs.flatMap((o) => o.points);
  const b = bounds(all);
  return {
    id, name, author, createdAt: Date.now(),
    objects: objs.map((o) => ({ ...o, points: o.points.map(([x, z]) => [x - b.x0, z - b.z0] as Pt) })),
  };
}

export interface PlaceOptions {
  /** block the template's centre goes to */
  at: Pt;
  /** quarter turns clockwise */
  turns: number;
  scale: number;
  mirror: boolean;
  /** scale path widths / building heights too (false = keep them) */
  scaleSizes: boolean;
}

/** Template → objects placed in the world (mirrored, scaled, rotated, centred on `at`). */
export function placeTemplate(t: Template, o: PlaceOptions): TemplateObject[] {
  const all = t.objects.flatMap((x) => x.points);
  const b = bounds(all);
  const cx = (b.x0 + b.x1) / 2, cz = (b.z0 + b.z1) / 2;
  const k = ((o.turns % 4) + 4) % 4;
  const tf = ([x, z]: Pt): Pt => {
    let dx = (x - cx) * o.scale, dz = (z - cz) * o.scale;
    if (o.mirror) dx = -dx;
    for (let i = 0; i < k; i++) [dx, dz] = [-dz, dx];
    return [Math.round(o.at[0] + dx), Math.round(o.at[1] + dz)];
  };
  return t.objects.map((x) => {
    if (x.schematic) {
      // the blocks can't be stretched: move its centre, turn it, keep its size
      const sb = bounds(x.points);
      const [cx2, cz2] = tf([(sb.x0 + sb.x1) / 2, (sb.z0 + sb.z1) / 2]);
      const ref = { ...x.schematic, ...turnSchematic(x.schematic, k, o.mirror) };
      const [w, l] = schematicDims(ref);
      return { ...x, schematic: ref, points: schematicFootprint(ref, Math.round(cx2 - (w - 1) / 2), Math.round(cz2 - (l - 1) / 2)) };
    }
    return {
    ...x,
    points: dedupe(x.points.map(tf) as V[]),
    ...(x.width !== undefined ? { width: o.scaleSizes ? Math.max(1, Math.round(x.width * o.scale)) : x.width } : {}),
    ...(x.height !== undefined ? { height: o.scaleSizes ? Math.max(1, Math.round(x.height * o.scale)) : x.height } : {}),
    };
  });
}

const rect = (x0: number, z0: number, x1: number, z1: number): Pt[] => [[x0, z0], [x1, z0], [x1, z1], [x0, z1]];

/** Starter templates (always available). */
export const BUILTIN_TEMPLATES: Template[] = [
  { id: "builtin-small-house", name: "Small house", builtin: true, objects: [
    { kind: "building", name: "Small house", points: rect(0, 0, 6, 6), height: 6, material: "minecraft:oak_planks" },
    { kind: "path", name: "Front path", points: [[3, 7], [3, 11]], width: 1, material: "minecraft:dirt_path" },
  ] },
  { id: "builtin-house", name: "Family house (L-shape)", builtin: true, objects: [
    { kind: "building", name: "House", points: [[0, 0], [12, 0], [12, 6], [6, 6], [6, 11], [0, 11]], height: 8, storey: 4, material: "minecraft:spruce_planks" },
    { kind: "area", name: "Garden", points: rect(7, 7, 14, 13), material: "minecraft:grass_block" },
    { kind: "path", name: "Front path", points: [[3, 12], [3, 16]], width: 2, material: "minecraft:gravel" },
  ] },
  { id: "builtin-farm", name: "Farm", builtin: true, objects: [
    { kind: "area", name: "Field", points: rect(0, 0, 8, 8), material: "minecraft:farmland" },
    { kind: "area", name: "Field", points: rect(10, 0, 18, 8), material: "minecraft:farmland" },
    { kind: "path", name: "Water channel", points: [[9, 0], [9, 8]], width: 1, material: "minecraft:water" },
    { kind: "building", name: "Barn", points: rect(0, 11, 11, 18), height: 8, material: "minecraft:dark_oak_planks" },
  ] },
  { id: "builtin-tower", name: "Watch tower", builtin: true, objects: [
    { kind: "building", name: "Tower", points: rect(0, 0, 4, 4), height: 22, storey: 5, material: "minecraft:cobblestone" },
  ] },
  { id: "builtin-market", name: "Market square", builtin: true, objects: [
    { kind: "area", name: "Market square", points: rect(0, 0, 20, 20), material: "minecraft:polished_andesite" },
    { kind: "building", name: "Stall", points: rect(2, 2, 5, 4), height: 4, material: "minecraft:oak_planks" },
    { kind: "building", name: "Stall", points: rect(15, 2, 18, 4), height: 4, material: "minecraft:oak_planks" },
    { kind: "building", name: "Stall", points: rect(2, 16, 5, 18), height: 4, material: "minecraft:oak_planks" },
    { kind: "building", name: "Stall", points: rect(15, 16, 18, 18), height: 4, material: "minecraft:oak_planks" },
    { kind: "marker", name: "Fountain", points: [[10, 10]] },
  ] },
];
